import { useState, useEffect } from 'react';
import type { FC } from 'react';
import { useSettings } from '../../contexts/SettingsContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../i18n/I18nContext';
import { LANGUAGES } from '../../i18n/languages';
import { apiClient } from '../../utils/api-client';
import { Dialog } from '../../components/ui/dialog';
import { Spinner } from '../../components/ui/spinner';
import { LoadingBar } from '../../components/ui/loading-bar';
import { Switch } from '../../components/ui/switch';

const SPEED_OPTIONS = [0.5, 0.75, 1.0, 1.25, 1.5, 2.0, 2.5, 3.0];

const IMAGE_STYLES = [
  { value: 'Studio Ghibli', key: 'image_style.studio_ghibli' },
  { value: 'Digital illustration', key: 'image_style.digital_illustration' },
  { value: 'Watercolor', key: 'image_style.watercolor' },
  { value: 'Oil painting', key: 'image_style.oil_painting' },
  { value: 'Photograph', key: 'image_style.photograph' },
  { value: 'Pencil sketch', key: 'image_style.pencil_sketch' },
  { value: 'Flat illustration', key: 'image_style.flat_illustration' },
  { value: 'Pop art', key: 'image_style.pop_art' },
];

const CURRENCY_CODES = ['USD', 'GBP', 'EUR'];
const CURRENCY_SYMBOLS: Record<string, string> = { USD: '$', GBP: '£', EUR: '€' };

interface SettingsDialogProps {
  open: boolean;
  onClose: () => void;
}

const SettingsDialog: FC<SettingsDialogProps> = ({ open, onClose }) => {
  const { settings, loading, updateSettings } = useSettings();
  const { user, refreshUser } = useAuth();
  const { t, setLanguage: setI18nLanguage } = useTranslation();

  // Local form state
  const [language, setLanguage] = useState(settings.language);
  const [audioVoice, setAudioVoice] = useState(settings.audioVoice);
  const [audioSpeed, setAudioSpeed] = useState(settings.audioSpeed);
  const [displayCurrency, setDisplayCurrency] = useState<string>(user?.displayCurrency || 'GBP');
  const [imageStyle, setImageStyle] = useState(settings.imageStyle);
  const [theme, setTheme] = useState<'light' | 'dark'>(settings.theme);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Sync local state from settings when dialog opens
  useEffect(() => {
    if (!open) return;
    setLanguage(settings.language);
    setAudioVoice(settings.audioVoice);
    setAudioSpeed(settings.audioSpeed);
    setDisplayCurrency(user?.displayCurrency || 'GBP');
    setImageStyle(settings.imageStyle);
    setTheme(settings.theme);
    setError('');
  }, [open, settings.language, settings.audioVoice, settings.audioSpeed, settings.imageStyle, settings.theme, user?.displayCurrency]);

  const isDirty =
    language !== settings.language ||
    audioVoice !== settings.audioVoice ||
    audioSpeed !== settings.audioSpeed ||
    imageStyle !== settings.imageStyle ||
    displayCurrency !== (user?.displayCurrency || 'GBP') ||
    theme !== settings.theme;

  const handleCancel = () => {
    onClose();
  };

  const handleSave = async () => {
    setError('');
    setSaving(true);
    try {
      const settingsDirty =
        language !== settings.language ||
        audioVoice !== settings.audioVoice ||
        audioSpeed !== settings.audioSpeed ||
        imageStyle !== settings.imageStyle ||
        theme !== settings.theme;

      if (settingsDirty) {
        await updateSettings({ language, audioVoice, audioSpeed, imageStyle, theme });
        if (language !== settings.language) {
          setI18nLanguage(language);
        }
      }

      if (displayCurrency !== (user?.displayCurrency || 'GBP')) {
        await apiClient.patch('/accounts/user/account', { displayCurrency });
        await refreshUser();
      }

      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('settings.save_failed'));
    } finally {
      setSaving(false);
    }
  };

  const footerContent = !loading && (
    <div className="flex items-center justify-end gap-2">
      <button
        type="button"
        onClick={handleCancel}
        disabled={saving}
        className="px-4 py-2 rounded-md text-sm border border-border text-foreground hover:bg-muted disabled:opacity-50 transition-colors"
      >
        {t('common.cancel')}
      </button>
      <button
        type="button"
        onClick={handleSave}
        disabled={saving || !isDirty}
        className={`px-5 py-2 rounded-md font-semibold text-sm transition-opacity ${
          isDirty
            ? 'bg-primary text-primary-foreground hover:opacity-90'
            : 'bg-muted text-muted-foreground'
        } disabled:opacity-50`}
      >
        {saving ? (
          <span className="flex items-center gap-2">
            <Spinner size="sm" className="border-primary-foreground" />
            {t('settings.saving')}
          </span>
        ) : (
          t('settings.update')
        )}
      </button>
    </div>
  );

  return (
    <Dialog open={open} onClose={handleCancel} title={t('settings.title')} disableClose={saving} footer={footerContent}>
      {loading ? (
        <LoadingBar visible />
      ) : (
        <div className="px-4 sm:px-6 py-4">
          {error && (
            <div className="bg-destructive text-destructive-foreground rounded-md p-3 text-sm mb-4">
              {error}
            </div>
          )}

          <section className="space-y-4">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t('settings.section_language')}
            </h3>

            <div>
              <label className="block text-sm font-medium mb-1">{t('settings.target_language')}</label>
              <p className="text-xs text-muted-foreground mb-2">{t('settings.target_language_description')}</p>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                disabled={saving}
                className="w-full border border-input rounded-md px-3 py-2 bg-background text-foreground text-sm"
              >
                {LANGUAGES.map((lang) => (
                  <option key={lang.code} value={lang.code}>{lang.name} ({lang.code})</option>
                ))}
              </select>
            </div>
          </section>

          <section className="space-y-4 mt-6 pt-6 border-t border-border">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t('settings.section_audio')}
            </h3>

            <div>
              <label className="block text-sm font-medium mb-1">{t('settings.audio_voice')}</label>
              <div className="flex gap-4">
                {['female', 'male'].map((voice) => (
                  <label key={voice} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="audioVoice"
                      value={voice}
                      checked={audioVoice === voice}
                      onChange={(e) => setAudioVoice(e.target.value)}
                      disabled={saving}
                      className="accent-primary"
                    />
                    <span className="text-sm capitalize">{t(`common.${voice}`)}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">{t('settings.audio_speed')}</label>
              <select
                value={audioSpeed}
                onChange={(e) => setAudioSpeed(parseFloat(e.target.value))}
                disabled={saving}
                className="w-full border border-input rounded-md px-3 py-2 bg-background text-foreground text-sm"
              >
                {SPEED_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s}x</option>
                ))}
              </select>
            </div>
          </section>

          <section className="space-y-4 mt-6 pt-6 border-t border-border">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t('settings.section_appearance')}
            </h3>

            <div>
              <label className="block text-sm font-medium mb-1">{t('settings.image_style')}</label>
              <select
                value={imageStyle}
                onChange={(e) => setImageStyle(e.target.value)}
                disabled={saving}
                className="w-full border border-input rounded-md px-3 py-2 bg-background text-foreground text-sm"
              >
                {IMAGE_STYLES.map((s) => (
                  <option key={s.value} value={s.value}>{t(s.key)}</option>
                ))}
              </select>
            </div>

            <div>
              <div className="flex items-center justify-between gap-3">
                <span className="flex-1">
                  <span className="block text-sm font-medium">{t('settings.dark_mode')}</span>
                  <span className="block text-xs text-muted-foreground mt-0.5">{t('settings.dark_mode_description')}</span>
                </span>
                <Switch
                  checked={theme === 'dark'}
                  onChange={(checked) => setTheme(checked ? 'dark' : 'light')}
                  disabled={saving}
                  aria-label={t('settings.dark_mode')}
                />
              </div>
            </div>
          </section>

          <section className="space-y-4 mt-6 pt-6 border-t border-border">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t('settings.section_account')}
            </h3>

            <div>
              <label className="block text-sm font-medium mb-1">{t('settings.display_currency')}</label>
              <p className="text-xs text-muted-foreground mb-2">{t('settings.display_currency_description')}</p>
              <select
                value={displayCurrency}
                onChange={(e) => setDisplayCurrency(e.target.value)}
                disabled={saving}
                className="w-full border border-input rounded-md px-3 py-2 bg-background text-foreground text-sm"
              >
                {CURRENCY_CODES.map((code) => (
                  <option key={code} value={code}>{CURRENCY_SYMBOLS[code]} {t(`onboarding.currency_${code.toLowerCase()}`)}</option>
                ))}
              </select>
            </div>
          </section>
        </div>
      )}
    </Dialog>
  );
};

export default SettingsDialog;
